import {
  FormsKpiStrip,
  FormsListPanel,
  FormsTopBarActions,
  filterFormsList,
  formsFilterCounts,
  useToast,
  type FormListFilter,
  type FormListItem,
} from '@elva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { keys } from '@/api/queryKeys';
import { errorStatus, extractApiErrorMessage } from '@/api/errors';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import { adaptFormStats, adaptFormTemplate, adaptFormPackageOptions } from '../adapters/formsAdapters';
import { SendFormDialog } from '../components/SendFormDialog';
import { ManagePackagesDrawer } from '../components/ManagePackagesDrawer';
import {
  DeleteFormConfirmDialog,
  type DeleteFormTarget,
} from '../components/DeleteFormConfirmDialog';
import { formsApi } from '../api/formsApi';
import { useFormQueue } from '../hooks/useFormQueue';
import { useFormPackages } from '../hooks/useFormPackages';
import { useFormStats } from '../hooks/useFormStats';
import { useDeleteFormTemplate, useFormTemplates } from '../hooks/useFormTemplates';

export function FormsListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteForm = useDeleteFormTemplate();
  const { data, isLoading, isError } = useFormTemplates();
  const { data: statsData } = useFormStats();
  const { data: queueData } = useFormQueue();
  const { data: packagesData } = useFormPackages();

  const forms = useMemo(
    () => (data?.results ?? []).map(adaptFormTemplate),
    [data?.results],
  );

  const kpiStats = useMemo(
    () => adaptFormStats(statsData, queueData?.count ?? queueData?.results?.length ?? 0),
    [statsData, queueData?.count, queueData?.results?.length],
  );

  const packageCount = Math.max(
    statsData?.package_count ?? 0,
    packagesData?.packages?.length ?? 0,
  );

  const [filter, setFilter] = useState<FormListFilter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sendTarget, setSendTarget] = useState<FormListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteFormTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [packagesOpen, setPackagesOpen] = useState(false);

  const packageFormOptions = useMemo(() => adaptFormPackageOptions(forms), [forms]);

  const filtered = useMemo(() => filterFormsList(forms, filter, search), [forms, filter, search]);
  const counts = useMemo(() => formsFilterCounts(forms), [forms]);

  if (isError) console.error('Failed to load form templates');

  usePageTopBar(
    {
      actions: (
        <FormsTopBarActions
          packageCount={packageCount}
          onManagePackages={() => {
            setPackagesOpen(true);
          }}
          onCreate={() => {
            void navigate('/forms/create');
          }}
        />
      ),
    },
    [packageCount],
  );

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(
      selected.size === filtered.length && filtered.length > 0
        ? new Set()
        : new Set(filtered.map((f) => f.id)),
    );
  };

  const reportDeleteError = (error: unknown) => {
    const message =
      errorStatus(error) === 409
        ? 'This form has open assignments and cannot be removed.'
        : extractApiErrorMessage(error);
    toast({ title: 'Delete failed', description: message, variant: 'error' });
  };

  const handleDelete = (id: string) => {
    const form = forms.find((f) => f.id === id);
    setDeleteTarget({ type: 'single', id, name: form?.name ?? 'this form' });
  };

  const handleBulkDelete = (ids: string[]) => {
    if (ids.length === 0) return;
    setDeleteTarget({ type: 'bulk', ids });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      if (deleteTarget.type === 'single') {
        await deleteForm.mutateAsync(deleteTarget.id);
        toast({ title: 'Form deleted', variant: 'success' });
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(deleteTarget.id);
          return next;
        });
      } else {
        const outcomes = await Promise.allSettled(
          deleteTarget.ids.map((id) => deleteForm.mutateAsync(id)),
        );
        const failed = outcomes.filter((o) => o.status === 'rejected').length;
        const removed = deleteTarget.ids.length - failed;
        if (removed > 0) toast({ title: `${removed} form(s) deleted`, variant: 'success' });
        if (failed > 0) {
          toast({
            title: 'Some forms not deleted',
            description: `${failed} form(s) have open assignments or could not be removed.`,
            variant: 'error',
          });
        }
        setSelected(new Set());
      }
      setDeleteTarget(null);
    } catch (error) {
      reportDeleteError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async (ids: string[]) => {
    if (ids.length === 0) return;
    const outcomes = await Promise.allSettled(
      ids.map((id) => formsApi.updateTemplate(id, { status: 'archived' })),
    );
    const failed = outcomes.filter((o) => o.status === 'rejected').length;
    const archived = ids.length - failed;
    if (archived > 0) toast({ title: `${archived} form(s) archived`, variant: 'success' });
    if (failed > 0) {
      toast({
        title: 'Some forms not archived',
        description: `${failed} form(s) could not be archived.`,
        variant: 'error',
      });
    }
    void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
    void queryClient.invalidateQueries({ queryKey: keys.formsStats });
    setSelected(new Set());
  };

  const pageLoading = isLoading && forms.length === 0;

  return (
    <div className="fm-root v6-root flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="relative z-[1] flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-[30px] pb-[22px] pt-4">
        {pageLoading ? (
          <ElvaPageLoader />
        ) : (
          <>
            <FormsKpiStrip stats={kpiStats} />
            <FormsListPanel
              forms={filtered}
              totalCount={forms.length}
              filter={filter}
              onFilterChange={setFilter}
              counts={counts}
              search={search}
              onSearchChange={setSearch}
              selected={selected}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onEdit={(id) => {
                void navigate(`/forms/create?edit=${id}`);
              }}
              onSend={setSendTarget}
              onDelete={handleDelete}
              onArchive={(ids) => {
                void handleArchive(ids);
              }}
              onBulkDelete={handleBulkDelete}
              onCreate={() => {
                void navigate('/forms/create');
              }}
            />
          </>
        )}
      </div>

      {sendTarget ? (
        <SendFormDialog
          form={sendTarget}
          onClose={() => {
            setSendTarget(null);
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteFormConfirmDialog
          target={deleteTarget}
          isDeleting={isDeleting}
          onClose={() => {
            if (!isDeleting) setDeleteTarget(null);
          }}
          onConfirm={() => {
            void confirmDelete();
          }}
        />
      ) : null}

      <ManagePackagesDrawer
        open={packagesOpen}
        onClose={() => {
          setPackagesOpen(false);
        }}
        formOptions={packageFormOptions}
      />
    </div>
  );
}
